import { StarIcon } from "@heroicons/react/24/outline";
import TextTooltip from "components/shared/TextTooltip";
import classNames from "components/ui/classNames";
import type {
  IAmericanCompanyIssuerObject,
  IAmericanReitIssuerObject,
  IBrazilianCompanyIssuerObject,
} from "lib/algolia/types/assets";
import useSelfie from "lib/hooks/useSelfie";
import Link from "next/link";

interface ICompanyCardProps {
  company: IAmericanCompanyIssuerObject | IAmericanReitIssuerObject | IBrazilianCompanyIssuerObject;
}

export default function CompanyCard({ company }: ICompanyCardProps) {
  const user = useSelfie();
  const linkAs =
    company.type === "AMERICAN_COMPANY" || company.type === "AMERICAN_REIT"
      ? `/us/${company.tickerSymbolPrefix.toLocaleLowerCase()}`
      : `/br/${company.tickerSymbolPrefix.toLocaleLowerCase()}`;
  const linkHref =
    company.type === "AMERICAN_COMPANY" || company.type === "AMERICAN_REIT"
      ? "/us/[tickerSymbol]"
      : "/br/[tickerSymbol]";

  return (
    <Link href={linkHref} as={linkAs}>
      <a
        href={linkAs}
        className={classNames(
          "flex h-full flex-col rounded-lg p-4 font-aeonik font-normal text-inherit shadow-md outline-none",
          "box-border select-none border-2 border-transparent bg-white no-underline dark:bg-gray-900",
          "hover:border-violet-600 dark:hover:border-gray-600",
          "focus:border-sky-500",
        )}
        css={{
          transition: "all 60ms ease 0s",
        }}
      >
        <div
          css={{
            display: "grid",
            gridColumnGap: 12,
            gridTemplateColumns: company.logoUrl ? "fit-content(100%) max-content" : "fit-content(100%)",
            justifyContent: "space-between",
          }}
        >
          <div className="flex flex-col space-y-1 overflow-hidden">
            <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-slate-600 dark:text-slate-400">
              {company.name}
            </h3>

            <span className="text-ellipsis whitespace-nowrap text-sm font-medium italic text-gray-500">
              {company.industrySector}
            </span>
          </div>

          {company.logoUrl && (
            <img
              role="presentation"
              src={company.logoUrl}
              onContextMenu={(event) => event.preventDefault()}
              onMouseDown={(event) => event.preventDefault()}
              width={63}
              height={19}
              alt={company.name}
              draggable={false}
              css={{
                objectFit: "contain",
                objectPosition: "top right",
                userSelect: "none",
                height: 19,
              }}
            />
          )}
        </div>

        {company.description && (
          <div className="mt-2">
            <div className="line-clamp-3">
              <p className="text-sm font-normal tracking-wider text-slate-500">{company.description}</p>
            </div>
          </div>
        )}

        <div className="mt-auto">
          <div className="mt-3">
            <div className="flex w-full items-center justify-between">
              <div className="rounded-md bg-violet-50 px-2 py-1 text-xs font-bold tracking-wider text-violet-600 dark:bg-gray-700 dark:text-gray-300">
                {company.tickerSymbolPrefix}
              </div>

              {user && user.isPremium && company.ratingSummary && company.ratingSummary.overallRating > 0 && (
                <TextTooltip
                  id={`tooltips/CompanyCard/ratingSummary/${company._id}`}
                  placement="top"
                  content={
                    company.ratingSummary.totalRatings === 1
                      ? `${company.ratingSummary.totalRatings} avaliação`
                      : `${company.ratingSummary.totalRatings} avaliações`
                  }
                >
                  {({ accessibility, bind, ref }) => (
                    <div className="flex items-center text-sm" {...accessibility} {...bind} ref={ref}>
                      <StarIcon className="-mt-[1px] h-4 w-4 fill-amber-500 text-amber-500 dark:fill-amber-500 dark:text-amber-500" />

                      <div className="ml-1">
                        <strong className="font-bold text-amber-600 dark:text-amber-600">
                          {company.ratingSummary?.overallRating}
                        </strong>
                      </div>
                    </div>
                  )}
                </TextTooltip>
              )}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
}
